import React, {
  ReactElement, useEffect, useRef, useState,
} from 'react';
import {
  ActivityIndicator, Modal, Platform, Pressable, ScrollView, StyleSheet, Text,
  TextInput, TouchableOpacity, View,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import CloseModalIcon from '../CloseModalIcon';
import UploadIcon from '../24pxIcons/UploadIcon';
import SearchIcon from '../SearchIcon';
import updateUserGroupUsers from '../../requests/Users/UpdateUserGroupUsers';
import AlertModal from '../AlertModal';
import { logEvent } from '../LogEvent';
import {
  FEATURE_COMING_SOON,
  USER_LIST_UPDATE_FAILURE,
} from '../../constants/AlertMessages';
import isMobile from '../IsMobile';
import nameRegex from '../Regex';
import CustomTextField from '../CustomTextField';
import addUserGroup from '../../requests/Users/AddUserGroup';
import GroupUserRowV2 from './GroupUserRowV2';
import DeleteIcon12 from '../12pxIcons/DeleteIcon12';
import PressableStyled from '../PressableStyled';

interface Props {
  modalVisible: any;
  users: any;
  existingGroup?: {
    groupId: string;
    title?: string;
    description?: string;
  };
  // eslint-disable-next-line no-unused-vars
  setModalVisible(value: any): any;
  // eslint-disable-next-line no-unused-vars
  updatedGroup(groupId: string, newTitle: string, newDesc: string, count: number): void;
}

const GroupCreateModal = (props: Props) => {
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const [groupNameFocused, setGroupNameFocused] = useState<boolean>(false);
  const [groupUserSearchValue, setAddGroupUserSearchValue] = useState<string>('');
  const [nameValue, setNameValue] = useState<string>(props.existingGroup?.title ?? '');
  const [descriptionValue, setDescriptionValue] = useState<string>(props.existingGroup?.description ?? '');
  const [users, setUsers] = useState(props.users ?? []);
  const [saving, setSaving] = useState<boolean>(false);
  const [titleValid, setTitleValid] = useState<boolean>(
    !!props.existingGroup?.title?.trim().length,
  );

  const maxTitleLength = 40;

  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertActionTitle, setAlertActionTitle] = useState<string | null>(null);
  const [alertAction, setAlertAction] = useState<() => void | null>(null);
  const [selectedTab, setSelectedTab] = useState<'ESSENTIAL_INFO' | 'MEMBERS'>(
    props.existingGroup?.groupId ? 'MEMBERS' : 'ESSENTIAL_INFO',
  );
  const [groupId, setGroupId] = useState<string>(props.existingGroup?.groupId ?? '');
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const initialGroupUserIdsRef = useRef<Set<string>>(new Set());

  const saveButtonDisabled: boolean = !titleValid;

  const showUploadImage = false;

  useEffect(() => {
    setUsers(props.users ?? []);
  }, [props.users]);

  useEffect(() => {
    setNameValue(props.existingGroup?.title ?? '');
    setDescriptionValue(props.existingGroup?.description ?? '');
    setGroupId(props.existingGroup?.groupId ?? '');
    setSelectedTab(props.existingGroup?.groupId ? 'MEMBERS' : 'ESSENTIAL_INFO');
    setTitleValid(!!props.existingGroup?.title?.trim().length);
  }, [props.existingGroup]);

  const showAlert = (
    title: string,
    error: boolean = true,
    message: string | ReactElement = '',
    actionTitle: string | null = null,
    action: () => void | null = null,
  ) => {
    setAlertTitle(title);
    setAlertError(error);
    setAlertMessage(message);
    setAlertActionTitle(actionTitle);
    setAlertAction(() => action);
    setSaving(false);
    setAlertVisible(true);
  };

  const restoreDefaults = () => {
    setGroupNameFocused(false);
    setAddGroupUserSearchValue('');
    setNameValue(props.existingGroup?.title ?? '');
    setDescriptionValue(props.existingGroup?.description ?? '');
    setSelectedTab(props.existingGroup?.groupId ? 'MEMBERS' : 'ESSENTIAL_INFO');
    const usersCopy = Object.assign([], props.users);
    // eslint-disable-next-line no-return-assign
    usersCopy.forEach((user) => user.selected = false);
    setUsers(usersCopy);
    setSaving(false);
    setTitleValid(!!props.existingGroup?.title?.trim().length);
    setGroupId(props.existingGroup?.groupId ?? '');
    setAlertVisible(false);
    setAlertError(true);
    setAlertTitle('');
    setAlertMessage('');
    setAlertActionTitle(null);
    setAlertAction(null);
  };

  React.useEffect(() => {
    setUsers(props.users ?? []);
  }, [props.users]);

  useEffect(() => {
    if (!groupId) {
      initialGroupUserIdsRef.current = new Set();
      return;
    }

    const initialGroupUserIds = new Set<string>();
    (props.users ?? []).forEach((user) => {
      if ((user.userGroupIds ?? []).includes(groupId)) {
        initialGroupUserIds.add(user.id);
      }
    });
    initialGroupUserIdsRef.current = initialGroupUserIds;
  }, [props.users, groupId]);

  function validateTitle(title) {
    let valid = true;
    if (
      title === undefined
      || title === null
      || title?.trim().length === 0
      || title?.trim().length > maxTitleLength
      || nameRegex.exp.test(title?.trim()) === false
    ) {
      valid = false;
      setTitleValid(false);
    } else if (
      nameValue?.toLocaleLowerCase() !== 'admin'
      && title?.trim().toLocaleLowerCase() === 'admin'
    ) {
      valid = false;
      setTitleValid(false);
    } else {
      setTitleValid(true);
    }
    return valid;
  }

  const save = () => {
    logEvent('GroupCreateModal', 'Save');
    if (saveButtonDisabled) return;
    setSaving(true);
    if (groupId) {
      const currentGroupUserIds = new Set<string>();
      users.forEach((user) => {
        const isInGroup = user.selected
          || (user.userGroupIds ?? []).includes(groupId);
        if (isInGroup) currentGroupUserIds.add(user.id);
      });

      const newlyAddedUserIds = [...currentGroupUserIds].filter(
        (userId) => !initialGroupUserIdsRef.current.has(userId),
      );
      const deletedUserIds = [...initialGroupUserIdsRef.current].filter(
        (userId) => !currentGroupUserIds.has(userId),
      );

      updateUserGroupUsers(
        groupId,
        newlyAddedUserIds,
        deletedUserIds,
        nameValue?.trim(),
        buildingId,
      ).then(
        (res) => {
          if (res?.isError) {
            showAlert(
              'Error',
              true,
              USER_LIST_UPDATE_FAILURE,
            );
          } else {
            setSaving(false);
            props.updatedGroup(
              groupId,
              nameValue?.trim(),
              descriptionValue,
              users.filter(
                (user) => user.selected
                  ?? (user.userGroupIds ?? []).indexOf(groupId) > -1,
              ).length,
            );
            restoreDefaults();
            props.setModalVisible(false);
          }
        },
      );
    } else {
      addUserGroup(nameValue?.trim(), descriptionValue, []).then((res) => {
        if (res && !res.isError) {
          setSaving(false);
          setGroupId(res.userGroupId);
          setSelectedTab('MEMBERS');
          props.updatedGroup(
            res.userGroupId,
            nameValue?.trim(),
            descriptionValue,
            users.filter(
              (user) => user.selected ?? (user.userGroupIds ?? [])
                .indexOf(res.userGroupId) > -1,
            ).length,
          );
          showAlert('Group created successfully', false);
        } else showAlert('Error', true, res?.errorText);
      });
      setAddGroupUserSearchValue('');
    }
  };

  const renderGroupDetails = () => (
    <View style={{ marginHorizontal: isMobile ? 16 : 0 }}>
      <Text style={{
        marginBottom: 12,
        fontFamily: 'Roboto_400Regular',
        fontSize: 14,
        color: Colors.light.hereworksBlack400,
      }}>
        Fields marked with * are mandatory
      </Text>
      {showUploadImage && <View style={{ flexDirection: 'row' }}>
        <TouchableOpacity
          style={{
            height: 64,
            width: 64,
            backgroundColor: Colors.light.hereworksDarkBlue,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 32,
          }}
          onPress={() => {
            logEvent('GroupCreateModal', 'Upload_Icon');
            showAlert(FEATURE_COMING_SOON, false);
          }}
        >
          <UploadIcon />
        </TouchableOpacity>
        <View style={{ justifyContent: 'center', marginLeft: 16 }}>
          <Text
            style={{
              marginBottom: 8,
              fontFamily: 'Roboto_400Regular',
              fontSize: 16,
              color: Colors.light.hereworksBlack600,
            }}
          >
            Upload an image
          </Text>
          <TouchableOpacity
            onPress={() => {
              logEvent('GroupCreateModal', 'Upload');
              showAlert(FEATURE_COMING_SOON, false);
            }}
          >
            <Text
              style={{
                fontFamily: 'Roboto_500Medium',
                fontSize: 12,
                color: Colors.light.hereworksBlue500,
              }}
            >
              Upload
            </Text>
          </TouchableOpacity>
        </View>
      </View>}
      <Text style={styles.textBoxHeaders}>Group name*</Text>
      <TextInput
        style={[
          styles.noteText,
          !isMobile
            ? {
              outlineWidth: 0,
              minWidth: 400,
            }
            : {},
          {
            borderColor: groupNameFocused ? Colors.light.hereworksBlue300
              : Colors.light.hereworksBlue100,
          },
        ]}
        placeholderTextColor={Colors.light.hereworksBlack400}
        placeholder={'Group name'}
        onFocus={() => setGroupNameFocused(true)}
        onBlur={() => setGroupNameFocused(false)}
        value={nameValue ?? ''}
        onChangeText={(text) => {
          validateTitle(text);
          setNameValue(text);
        }}
      />
      {nameValue?.trim().length > maxTitleLength && (
        <Text style={[styles.validationText, { color: Colors.light.hereworksUrgentRed }]}>
          {`${nameValue?.trim().length ?? nameValue?.trim().length ?? 0}/${maxTitleLength}`}
        </Text>
      )}
      <Text style={styles.textBoxHeaders}>Description</Text>
      <CustomTextField
        maxLength={150}
        indicateLength
        style={[
          styles.noteText,
          { minHeight: 80 },
        ]}
        placeholder={'Description'}
        value={descriptionValue ?? ''}
        updateValue={(text) => {
          setDescriptionValue(text);
        }}
        multiline={true}
      />
    </View>
  );

  const renderGroupMembers = () => (
    <View
      style={{
        marginLeft: isMobile ? 16 : 0,
        marginRight: isMobile ? 16 : 0,
        position: 'relative',
        zIndex: 20,
      }}
    >
      <View
        style={[
          styles.dropdownBox,
          { zIndex: 1, marginTop: 0, width: isMobile ? undefined : 450 },
        ]}
      >
        <View style={{ alignItems: 'flex-end', marginLeft: 12 }}>
          <SearchIcon />
        </View>
        <TextInput
          style={[
            styles.dropdownBoxText,
            !isMobile && {
              outlineWidth: 0,
              outlineColor: 'transparent',
              marginLeft: 8,
            },
          ]}
          placeholder={'Search by username or email address...'}
          testID='um-add-group-search-bar'
          placeholderTextColor={Colors.light.hereworksBlack400}
          value={groupUserSearchValue}
          onChangeText={(text) => {
            setAddGroupUserSearchValue(text);
          }}
          onFocus={() => setSearchFocused(true)}
        />
        <PressableStyled
          style={{ alignItems: 'flex-end', marginRight: 15 }}
          onPress={() => {
            setAddGroupUserSearchValue('');
            setSearchFocused(false);
          }}
        >
          <DeleteIcon12 color={Colors.light.hereworksBlack400} />
        </PressableStyled>
      </View>
      {users.filter((user) => user.selected).length > 0 && (
        <View style={{ maxHeight: 100, marginTop: 10 }}>
          <ScrollView indicatorStyle={'black'} nestedScrollEnabled={true}>
            {(users ?? [])
              .filter((user) => `${user.firstName}`.toLocaleLowerCase()
                .includes(groupUserSearchValue.toLocaleLowerCase()))
              .filter((user) => user.selected)
              .map((user) => (
              <View key={`${user.email}${user.selected}`}>
                <GroupUserRowV2
                  name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
                  email={user.email}
                  icon={user.icon}
                  selected={user.selected ? true
                    : (user.userGroupIds ?? []).indexOf(groupId) > -1}
                  setSelected={(selected) => {
                    const updatedUsers = users.map((u) => {
                      if (u.email !== user.email) return u;

                      const updatedUser = { ...u, selected };

                      if (!selected) {
                        const updatedGroupIds = (u.userGroupIds
                          ?? []).filter((g) => g !== groupId);
                        updatedUser.userGroupIds = updatedGroupIds;
                        updatedUser.deleted = true;
                      }

                      return updatedUser;
                    });
                    setUsers(updatedUsers);
                  }}
                />
              </View>
              ))}
        </ScrollView>
      </View>)}
      {searchFocused && (
        <View style={{
          position: 'absolute',
          backgroundColor: '#fff',
          borderRadius: 10,
          top: 45,
          left: 0,
          right: 0,
          zIndex: 50,
          elevation: 50,
        }}>
        <ScrollView
          indicatorStyle={'black'}
          nestedScrollEnabled={true}
          style={{
            maxHeight: 220,
            padding: 10,
          }}
        >
          {(users ?? [])
            .filter((user) => `${user.firstName}`.toLocaleLowerCase()
              .includes(groupUserSearchValue.toLocaleLowerCase()) || `${user.email}`.toLocaleLowerCase()
              .includes(groupUserSearchValue.toLocaleLowerCase()))
            .filter((user) => !user.selected).length === 0 && (
              <View style={{ alignItems: 'center', marginTop: 40 }}>
                <Text style={{ color: Colors.light.hereworksBlack400 }}>
                  No users found
                </Text>
              </View>
          )}
          {(users ?? [])
            .filter((user) => `${user.firstName}`.toLocaleLowerCase()
              .includes(groupUserSearchValue.toLocaleLowerCase()) || `${user.email}`.toLocaleLowerCase()
              .includes(groupUserSearchValue.toLocaleLowerCase()))
            .filter((user) => !user.selected)
            .map((user) => (
              <View key={`${user.email}${user.selected}`}>
                <GroupUserRowV2
                  name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
                  email={user.email}
                  icon={user.icon}
                  selected={user.selected ? true
                    : (user.userGroupIds ?? []).indexOf(groupId) > -1}
                  setSelected={(selected) => {
                    const updatedUsers = users.map((u) => {
                      if (u.email !== user.email) return u;

                      const updatedUser = { ...u, selected };

                      if (!selected) {
                        const updatedGroupIds = (u.userGroupIds
                          ?? []).filter((g) => g !== groupId);
                        updatedUser.userGroupIds = updatedGroupIds;
                        updatedUser.deleted = true;
                      }

                      return updatedUser;
                    });
                    setAddGroupUserSearchValue('');
                    setUsers(updatedUsers);
                    setSearchFocused(false);
                  }}
                />
              </View>
            ))}
        </ScrollView>
      </View>)}
    </View>
  );

  const renderSaveCancel = () => (
    <View
      style={{
        flexDirection: 'row',
        flex: isMobile ? 1 : undefined,
        position: 'relative',
        zIndex: 1,
      }}
    >
      {isMobile ? null : <TouchableOpacity
        style={{
          backgroundColor: '#fff',
          borderRadius: 4,
          borderWidth: 1,
          borderColor: Colors.light.hereworksLightBlue,
        }}
        onPress={() => {
          logEvent('GroupCreateModal', 'Cancel');
          setAddGroupUserSearchValue('');
          restoreDefaults();
          props.setModalVisible(false);
        }}
      >
        <Text
          style={{
            color: Colors.light.hereworksLightBlue,
            fontFamily: 'Roboto_400Regular',
            fontSize: 16,
            marginVertical: 8,
            marginHorizontal: 24,
          }}
        >
          Cancel
        </Text>
      </TouchableOpacity>}
      <TouchableOpacity
        style={{
          flex: isMobile ? 1 : undefined,
          backgroundColor: Colors.light.hereworksLightBlue,
          borderRadius: 4,
          marginLeft: isMobile ? 24 : 12,
          marginRight: isMobile ? 24 : 0,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: !saveButtonDisabled ? 1 : 0.4,
        }}
        disabled={saveButtonDisabled}
        testID='save-btn'
        onPress={save}
      >
        <Text
          style={{
            color: saving ? Colors.light.hereworksLightBlue : '#fff',
            fontFamily: 'Roboto_400Regular',
            fontSize: 16,
            marginVertical: 8,
            marginHorizontal: 24,
          }}
        >
          {selectedTab === 'ESSENTIAL_INFO' ? 'Create' : 'Add members'}
        </Text>
        {saving && <ActivityIndicator style={{ position: 'absolute' }} color="#fff" />}
      </TouchableOpacity>
    </View>
  );

  const renderModalContent = () => (
    <View
      style={{ alignSelf: 'stretch', justifyContent: 'space-between', flex: 1 }}>
      <View
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          alignSelf: isMobile ? 'stretch' : 'center',
          borderRadius: 10,
          backgroundColor: '#fff',
          marginTop: isMobile ? 16 : 0,
        }}
      >
        <Text
          style={{
            marginTop: isMobile ? 19 : 60,
            marginBottom: isMobile ? 19 : 32,
            marginHorizontal: isMobile ? 104 : 0,
            fontSize: isMobile ? 20 : 28,
            fontFamily: 'Poppins_600SemiBold',
            color: Colors.light.hereworksBlack600,
          }}
        >
          { selectedTab === 'ESSENTIAL_INFO' ? 'Create group' : 'Add members' }
        </Text>
        <View
          style={{
            flexDirection: isMobile ? 'column' : 'row',
            marginHorizontal: isMobile ? 0 : 40,
            alignSelf: 'stretch',
            position: 'relative',
            zIndex: selectedTab === 'MEMBERS' ? 20 : 1,
          }}
        >
          {selectedTab === 'ESSENTIAL_INFO' && renderGroupDetails()}
          {selectedTab === 'MEMBERS' && renderGroupMembers()}
        </View>
        <View style={{
          alignSelf: 'stretch',
          flexDirection: 'row',
          justifyContent: 'center',
          marginVertical: isMobile ? 25 : 40,
          marginHorizontal: isMobile ? 0 : 40,
        }}>
          {renderSaveCancel()}
        </View>
        <Pressable
          onPress={() => {
            logEvent('GroupCreateModal', 'Close_Modal');
            restoreDefaults();
            props.setModalVisible(false);
          }}
          testID={nameValue === undefined ? 'um-close-create-user-group-modal-btn'
            : 'um-close-edit-user-group-modal-btn'}
          style={(state) => [
            Style.closeButton, state.hovered && Style.hovered, state.pressed && Style.pressed]}
        >
          <CloseModalIcon />
        </Pressable>
      </View>
    </View>
  );

  return (
    <Modal
      visible={props.modalVisible}
      style={{ margin: 10, justifyContent: isMobile ? 'flex-start' : 'center', alignItems: 'center' }}
      onRequestClose={() => props.setModalVisible(false)}
      presentationStyle='pageSheet'
      animationType={isMobile ? 'slide' : 'fade'}
      transparent={!isMobile}
      onDismiss={restoreDefaults}
    >
      {isMobile ? <ScrollView
        style={{ width: '100%' }}
        contentContainerStyle={{ flexGrow: 1 }}
        nestedScrollEnabled
      >
        {renderModalContent()}
      </ScrollView> : <Pressable
        onPress={() => props.setModalVisible(false)}
        style={{
          flex: 1, justifyContent: 'center', paddingHorizontal: 40, backgroundColor: '#00000030',
        }}
      >
        <Pressable onPress={(e) => e.stopPropagation()} style={{ cursor: 'auto' }}>
          {renderModalContent()}
        </Pressable>
      </Pressable>}
      <AlertModal
        modalVisible={alertVisible}
        error={alertError}
        title={alertTitle}
        message={alertMessage}
        action={alertAction}
        actionTitle={alertActionTitle}
        setModalVisible={setAlertVisible}
        mobileFullScreen={isMobile}
        autoClose={!alertAction}
      />
    </Modal>
  );
};

const styles = StyleSheet.create({
  noteText: {
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
    fontFamily: 'Roboto_400Regular',
    alignItems: 'center',
    fontSize: 16,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    backgroundColor: Colors.light.plainBackground,
    borderWidth: 1,
    alignContent: 'flex-start',
    marginVertical: 5,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 5,
    verticalAlign: 'top',
  },
  textBoxHeaders: {
    marginTop: 12,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  dropdownBox: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    marginTop: 24,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginLeft: 12,
    flex: 1,
    alignSelf: 'stretch',
  },
  validationText: {
    marginTop: 4,
    color: Colors.light.hereworksBlack300,
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 20,
  },
});

export default GroupCreateModal;
